import { Box, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { isBefore } from 'date-fns';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import {
  BookingTable,
  IBookingTableItem,
  IBookingTableItemCheckin,
} from '../../../../../../../../../../../components/BookingTable';
import { useActivity } from '../../../../../../../../../../../hooks/activity';
import {
  IBookingPaymentBase,
  IBookingSessionBlockBookingBase,
} from '../../../../../../../../../../../models/bookings';
import { listBookingsService } from '../../../../../../../../../../../services/Bookings/ListBookingsService';
import {
  IAffectedSessionBlockReservation,
  listAffectedSessionBlockReservationsService,
} from '../../../../../../../../../../../services/Activities/ListAffectedSessionBlockReservationsService';
import { translateError } from '../../../../../../../../../../../utils/errors';
import { maskDateTime } from '../../../../../../../../../../../utils/formatters/handleMask';

interface ILoadBookingsProps {
  activityId: string;
  sessionBlockOnly?: boolean;
  spotName?: string;
  showPendingBookingsOnly?: boolean;
  userDocument?: string;
  userName?: string;
  sort?: 'bookedDate' | 'createdAt';
  order?: 'ASC' | 'DESC';
  page?: number;
}

export const ActivityBookings = (): JSX.Element => {
  const toast = useToast();
  const { activity } = useActivity();

  const [bookingsList, setBookingsList] = useState<IBookingTableItem[]>([]);
  const [sessionBlockReservations, setSessionBlockReservations] = useState<
    IAffectedSessionBlockReservation[]
  >([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>(
    'DESC',
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('bookedDate');
  const [showPaymentPending, setShowPaymentPending] = useState(false);
  const [showPendingOnly, setShowPendingOnly] = useState(true);
  const [showSessionBlocks, setShowSessionBlocks] = useState(false);
  const [sortBy, setSortBy] = useState<'bookedDate' | 'createdAt'>(
    'bookedDate',
  );
  const [totalPages, setTotalPages] = useState<number>();

  const loadBookings = useCallback(
    async ({
      activityId,
      sessionBlockOnly,
      spotName,
      userDocument,
      userName,
      showPendingBookingsOnly,
      order,
      page,
      sort = 'bookedDate',
    }: ILoadBookingsProps) => {
      try {
        if (sessionBlockOnly) {
          const reservations =
            await listAffectedSessionBlockReservationsService({
              activityId,
              limit: 10,
              page,
            });

          setBookingsList([]);
          setTotalPages(reservations.pages);
          setSessionBlockReservations(reservations.items);

          return;
        }

        const bookings = await listBookingsService({
          activityId,
          sessionBlockOnly,
          spotName,
          userDocument: userDocument?.replace(/\D/g, ''),
          userName,
          showPendingBookingsOnly,
          limit: 10,
          page,
          sort,
          order,
        });

        const parsedBookings = bookings.items.map((booking) => {
          const [bookedYear, bookedMonth, bookedDay] =
            booking.bookedDate.split('-');

          const bookedDateTime = new Date(
            booking?.bookedDate.concat(
              `T${booking.activitySchedule.startTime}`,
            ) || '',
          );

          const isCancelable =
            !booking?.checkins.length &&
            isBefore(new Date(), bookedDateTime) &&
            !booking?.deletedAt &&
            !booking.sessionBlockBookings?.length;

          return {
            ...booking,
            checkins: booking.checkins.map((checkin) => ({
              ...checkin,
              formattedCreatedAt: maskDateTime(checkin.createdAt),
            })),
            formattedBookedDate: `${bookedDay}/${bookedMonth}/${bookedYear}`,
            formattedCreatedAt: maskDateTime(booking.createdAt),
            formattedDeletedAt: booking.deletedAt
              ? maskDateTime(booking.deletedAt)
              : undefined,
            formattedEndTime: booking.endTime
              ? booking.endTime.slice(11, 16)
              : booking.activitySchedule.endTime.slice(0, 5),
            formattedStartTime: booking.startTime
              ? booking.startTime.slice(11, 16)
              : booking.activitySchedule.startTime.slice(0, 5),
            isCancelable,
          };
        });

        setTotalPages(bookings.pages);
        setBookingsList(parsedBookings);
        setSessionBlockReservations([]);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados das reservas, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    setTotalPages(1);
    setBookingsList([]);
    setSessionBlockReservations([]);

    if (activity) {
      loadBookings({
        ...currentFilters,
        activityId: activity.id,
        sessionBlockOnly: showSessionBlocks,
        showPendingBookingsOnly: showPendingOnly,
        order: currentOrdering,
        page: currentPage,
        sort: 'bookedDate',
      });
    }
  }, [
    currentPage,
    loadBookings,
    currentOrdering,
    currentFilters,
    activity,
    showSessionBlocks,
    showPendingOnly,
  ]);

  useEffect(() => {
    const handleSessionBlocksChanged = (event: Event): void => {
      const customEvent = event as CustomEvent<{ activityId: string }>;

      if (!activity || customEvent.detail.activityId !== activity.id) {
        return;
      }

      loadBookings({
        ...currentFilters,
        activityId: activity.id,
        sessionBlockOnly: showSessionBlocks,
        showPendingBookingsOnly: showPendingOnly,
        order: currentOrdering,
        page: currentPage,
        sort: 'bookedDate',
      });
    };

    window.addEventListener(
      'activity-session-blocks:changed',
      handleSessionBlocksChanged,
    );

    return () => {
      window.removeEventListener(
        'activity-session-blocks:changed',
        handleSessionBlocksChanged,
      );
    };
  }, [
    activity,
    currentFilters,
    currentOrdering,
    currentPage,
    loadBookings,
    showPendingOnly,
    showSessionBlocks,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters(value ? { [filterBy]: value } : undefined);
    },
    [filterBy],
  );
  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleSortList = useCallback((sort: 'bookedDate' | 'createdAt') => {
    setSortBy(sort);
  }, []);

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleBookingPayment = (payment: IBookingPaymentBase): void => {
    setBookingsList((prevState) =>
      prevState.map((booking) =>
        booking.id === payment.bookingId ? { ...booking, payment } : booking,
      ),
    );
  };

  const handleCheckin = (bookingCheckin: IBookingTableItemCheckin): void => {
    setBookingsList((prevState) =>
      prevState.map((booking) =>
        booking.id === bookingCheckin.bookingId
          ? {
              ...booking,
              checkins: [...booking.checkins, bookingCheckin],
              isCancelable: false,
            }
          : booking,
      ),
    );
  };

  const handleDeleteBooking = (booking: IBookingTableItem): void => {
    setBookingsList((prevState) =>
      prevState.filter((book) => book.id !== booking.id),
    );
  };

  const handleSessionBlockBookingUpdate = (
    bookingId: string,
    sessionBlockBooking: IBookingSessionBlockBookingBase,
  ): void => {
    setBookingsList((prevState) =>
      prevState.map((booking) =>
        booking.id === bookingId
          ? {
              ...booking,
              deletedAt:
                sessionBlockBooking.status === 'DECLINED_BY_USER'
                  ? new Date().toISOString()
                  : booking.deletedAt,
              sessionBlockBookings: [sessionBlockBooking],
            }
          : booking,
      ),
    );
  };

  const handleSessionBlockReservationUpdate = (
    reservationId: string,
    sessionBlockBooking: IBookingSessionBlockBookingBase,
  ): void => {
    setSessionBlockReservations((prevState) =>
      prevState.map((reservation) =>
        reservation.reservation.id === reservationId
          ? {
              ...reservation,
              impactStatus: sessionBlockBooking.status,
              reservation: {
                ...reservation.reservation,
                deletedAt:
                  sessionBlockBooking.status === 'DECLINED_BY_USER'
                    ? new Date().toISOString()
                    : reservation.reservation.deletedAt,
                status: sessionBlockBooking.status,
              },
            }
          : reservation,
      ),
    );
  };

  return (
    <Box>
      <BookingTable
        mt="4"
        bookings={bookingsList}
        currentPage={currentPage}
        filterBy={filterBy}
        filterByOptions={[
          {
            label: 'Nome',
            value: 'userName',
          },
          {
            label: 'Documento',
            value: 'userDocument',
          },
          {
            label: 'Horário reservado',
            value: 'bookedDate',
          },
        ]}
        onChangeFilterBy={handleFilterBy}
        onCheckin={handleCheckin}
        onDeleteBooking={handleDeleteBooking}
        onFilter={debounceHandleFilter}
        onOrder={handleOrderList}
        onPageChange={handlePageChange}
        onPay={handleBookingPayment}
        onSessionBlockBookingUpdate={handleSessionBlockBookingUpdate}
        onSessionBlockReservationUpdate={handleSessionBlockReservationUpdate}
        onSort={handleSortList}
        onShowSessionBlocks={() => {
          setCurrentPage(1);
          setShowPaymentPending(false);
          setShowSessionBlocks((prevState) => !prevState);
        }}
        onShowPaymentPending={() =>
          setShowPaymentPending((prevState) => !prevState)
        }
        onShowPending={() => setShowPendingOnly((prevState) => !prevState)}
        order={currentOrdering}
        showActivityColumn={false}
        showPaymentPending={showPaymentPending}
        showPending={showPendingOnly}
        showSessionBlocks={showSessionBlocks}
        sessionBlockReservations={sessionBlockReservations}
        sortBy={sortBy}
        totalPages={totalPages}
      />
    </Box>
  );
};
